import { useEffect, useMemo, useState } from 'react'

import { supportedLanguages, formatPriceText, Receipt, PURCHASE_STATUS } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'

import { useFetchPendingPurchasesQuery, useFetchReceiptsQuery } from '@/api'
import { ShoppingBag } from '@/components/Icons'
import { Loader } from '@/components/Loader'
import { useLocale } from '@/features/locale/useLocale'
import { isRefunded } from '@/utils/purchase'
import { COLOR_BLUE } from '@/utils/styles'
import { useAppSelector } from '@/utils/useAppSelector'

import CollapsibleContainer from '../../components/CollapsibleContainer'
import { delayMs } from '../../utils/delayMs'
import { LOGIN_STATUS } from '../user/userSlice'
import { RowAlert } from './RowAlert'

import styles from './Purchases.module.scss'

const FIVE_SECONDS_MILLIS = 5000
const MINUTE_MILLIS = 60000

const getRowAlertCopyKey = (status: PURCHASE_STATUS) => {
  switch (status) {
    case PURCHASE_STATUS.REFUND_WEBHOOK:
      return 'acc_phi_re_msg' as const
    case PURCHASE_STATUS.CHARGEBACK_WEBHOOK:
      return 'acc_phi_ch_msg' as const
    default:
      return null
  }
}

const useFetchPurchases = (isLoggedIn: boolean, onPendingCountChanged: () => void) => {
  const [previousResultCount, setPreviousResultCount] = useState(0)
  const pollingInterval = previousResultCount > 0 ? FIVE_SECONDS_MILLIS : MINUTE_MILLIS
  const response = useFetchPendingPurchasesQuery(undefined, {
    // When pending purchase is found poll every five seconds
    // Otherwise poll every minute
    pollingInterval: delayMs(pollingInterval, pollingInterval / 10),
    refetchOnMountOrArgChange: true,
    skip: !isLoggedIn,
  })
  const resultCount = response.data?.length ?? 0
  useEffect(() => {
    if (previousResultCount !== resultCount) {
      onPendingCountChanged()
    }
    setPreviousResultCount(resultCount)
  }, [previousResultCount, resultCount, onPendingCountChanged])
  return response
}
/**
 * We return 20 or less purchases on each request
 * but for most users the 20 most recent purchases are returned
 */
const usePagedFetchPurchases = (isLoggedIn: boolean) => {
  const [next, setNext] = useState<{ timestamp: number; purchaseId: string } | undefined>(undefined)
  const [receiptsStore, setStore] = useState<Receipt[]>([])
  const { data, ...rest } = useFetchReceiptsQuery(next, { skip: !isLoggedIn, refetchOnMountOrArgChange: true })
  useEffect(() => {
    if (!data) {
      return
    }
    const { receipts } = data

    setStore((store) => store.concat(receipts))
  }, [data, setStore, setNext])

  const nextPage = useMemo(() => {
    const { lastEvaluatedKey } = data ?? {}
    if (!lastEvaluatedKey) {
      return undefined
    }
    return () => setNext(lastEvaluatedKey)
  }, [data])

  return {
    receipts: receiptsStore,
    ...rest,
    nextPage,
  }
}

export const PurchasesContainer: React.FC = () => {
  const isLoggedIn = useAppSelector((state) => state.user.loginStatus === LOGIN_STATUS.LOGGED_IN)
  const locale = useLocale()
  const t = useTranslations()

  const { receipts, nextPage, isFetching, refetch } = usePagedFetchPurchases(isLoggedIn)
  const { data: pendingPurchases, isLoading: isLoadingHistory } = useFetchPurchases(isLoggedIn, refetch)

  const noPurchaseHistory = (receipts?.length ?? 0) === 0

  return (
    <CollapsibleContainer
      className={styles.ContentContainer}
      title={t('acc_purchase_title')}
      data-cy="purchase-history-collapsible"
      loading={isLoadingHistory && isLoadingHistory}
      defaultOpen
    >
      <ul data-cy="pending-purchases">
        {pendingPurchases?.map((purchase) => (
          <li className={styles.Purchase} key={purchase.id} data-cy={`pending-purchase-${purchase.id}`}>
            <div className={styles.PurchaseRowIcon}>
              <Loader strokeColor={COLOR_BLUE} />
            </div>
            <div className={styles.PurchaseRowTitle}>{t('pending_tit')}</div>
            <div className={styles.PurchaseRowInfo}>
              <span>{purchase.id}</span>
            </div>
            <div className={styles.PurchaseRowPrice}>
              {formatPriceText(purchase.checkout.total, locale, purchase.checkout.currency)}
            </div>
            <div />
          </li>
        ))}
        {receipts?.map((receipt) => (
          <li
            className={cx(styles.Purchase, { [styles.Refunded]: isRefunded(receipt.status) })}
            key={receipt.purchaseId}
          >
            <div className={styles.PurchaseRowIcon}>
              <ShoppingBag />
            </div>
            <div className={styles.PurchaseRowTitle}>
              {new Intl.DateTimeFormat(supportedLanguages[locale].locale, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(receipt.timestamp * 1000 ?? 0))}
            </div>
            <div className={styles.PurchaseRowInfo}>
              <span>
                {t('acc_order')} # {receipt.purchaseId}
              </span>
            </div>
            <div className={styles.PurchaseRowPrice}>
              {formatPriceText(receipt.total, receipt.language, receipt.currency, false)}
            </div>
            <div className={styles.PurchaseRowReceipt} data-cy={`my-purchase-${receipt.purchaseId}`}>
              <a href={`/${locale}/receipt?purchaseId=${receipt.purchaseId}`} target="_blank" rel="noreferrer">
                {t('acc_view_receipt')}
              </a>
            </div>
            <ReceiptAlert receipt={receipt} />
          </li>
        ))}
        <LoadMore loading={isFetching} nextPage={nextPage}>
          {t('account_load_more')}
        </LoadMore>
        {!isLoadingHistory && noPurchaseHistory && (
          <li className={cx(styles.Purchase, styles.Empty)}>{t('acc_purchase_missing')}</li>
        )}
      </ul>
    </CollapsibleContainer>
  )
}

const ReceiptAlert = ({ receipt }: { receipt: Receipt }) => {
  const t = useTranslations()
  const alertCopy = getRowAlertCopyKey(receipt.status)
  if (!alertCopy) return null
  return <RowAlert className={styles.PurchaseRowRefund} content={t(alertCopy)} />
}

const LoadMore: React.FC<React.PropsWithChildren<{ loading: boolean; nextPage?: () => void }>> = ({
  children,
  loading,
  nextPage,
}) => {
  if (!nextPage) {
    return null
  }
  return (
    <div className={styles.PurchasesLoadMore}>
      {loading ? <Loader strokeColor={COLOR_BLUE} /> : <button onClick={nextPage}>{children}</button>}
    </div>
  )
}
